import { Navigate, RouteObject, useParams } from 'react-router-dom';
import { PATH, PRIVATE_PATH } from '../shared/constants/paths.constant';
import { PlanGuard } from '../shared/guards/plan/plan.guard';
import { RentalsAddonGate } from '../pages/private/rentals/components/rentals-addon-gate';
import { lazyWithRetry } from '../shared/utils/lazy-with-retry';
import { createSuspendedRoute } from '../shared/utils/suspended-route.utils';

const NotFound = lazyWithRetry(
  () => import('../pages/public/notFound/not-found.page')
);
const Dashboard = lazyWithRetry(
  () => import('../pages/private/dashboard/dashboard.page')
);
const ContractSigning = lazyWithRetry(
  () => import('../pages/private/contract-signing/contract-signing.page')
);
const POS = lazyWithRetry(() => import('../pages/private/pos/pos.page'));
// Commercial-document dispatcher — routes `/invoices/new?type=X`
// (and edit route) to the correct per-document page. Order goes to
// SalesOrderDetail; invoice + quote stay on the existing InvoicePage.
const Invoice = lazyWithRetry(() =>
  import('../pages/private/invoice/commercial-document.dispatcher').then(
    (m) => ({
      default: m.CommercialDocumentDispatcher,
    })
  )
);
// Remove InvoiceView and InvoiceEdit - now using the same Invoice component
const InvoiceList = lazyWithRetry(
  () => import('../pages/private/invoice/invoice-list.page')
);
const SalesOrdersPage = lazyWithRetry(
  () => import('../pages/private/sales-orders/sales-orders.page')
);
const RentalListPage = lazyWithRetry(
  () => import('../pages/private/rentals/rental-list.page')
);
const RentalDetailPage = lazyWithRetry(
  () => import('../pages/private/rentals/rental-detail.page')
);
const RentalsCalendarPage = lazyWithRetry(
  () => import('../pages/private/rentals/calendar.page')
);
const RentalsRecogidoPage = lazyWithRetry(
  () => import('../pages/private/rentals/recogido.page')
);

const Products = lazyWithRetry(
  () => import('../pages/private/inventory/products/products.page')
);
const EditProduct = lazyWithRetry(
  () =>
    import('../pages/private/inventory/products/edit-product/edit-product.page')
);
const ProductIngredients = lazyWithRetry(
  () => import('../pages/private/inventory/products/product-ingredients.page')
);
const Categories = lazyWithRetry(
  () => import('../pages/private/inventory/categories/categories.page')
);
const Warehouse = lazyWithRetry(
  () => import('../pages/private/inventory/warehouse/warehouse.page')
);
const KdsPage = lazyWithRetry(
  () => import('../pages/private/inventory/kds/kds.page')
);
const KdsReportsPage = lazyWithRetry(
  () => import('../pages/private/inventory/kds/reports/kds-reports.page')
);
const StockMovementPage = lazyWithRetry(
  () => import('../pages/private/inventory/stock-movement/stock-movement.page')
);
const FollowUpRemindersPage = lazyWithRetry(
  () => import('../pages/private/follow-up-reminders/follow-up-reminders.page')
);
const PurchaseOrdersPage = lazyWithRetry(
  () => import('../pages/private/purchase-orders/purchase-orders.page')
);
const EditPurchaseOrdersPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/purchase-orders/edit-purchase-orders/edit-purchase-orders.page'
    )
);
// Keyed on the :id param so moving between documents (detail → "Crear
// Factura" → /new) mounts a fresh form instead of carrying the previous
// document's state across the same route element.
const EditPurchaseOrdersRoute = () => {
  const { id } = useParams<{ id: string }>();
  return <EditPurchaseOrdersPage key={id} />;
};
const ProcurementPurchaseOrdersPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/procurement-purchase-orders/procurement-purchase-orders.page'
    )
);
const EditProcurementPurchaseOrderPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/procurement-purchase-orders/edit-procurement-purchase-order/edit-procurement-purchase-order.page'
    )
);
// Supplier tax document detail — mounted under /reports/dgii. The
// former standalone list page was removed because the DGII 606 report
// already lists the same rows; row click drills into this detail
// page (also serves as the supplier payment register).
const SupplierTaxDocumentDetailPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/supplier-tax-documents/supplier-tax-document-detail.page'
    )
);
const UsersPage = lazyWithRetry(
  () => import('../pages/private/users/users.page')
);
const ShiftsPage = lazyWithRetry(
  () => import('../pages/private/configuration/shifts/shifts.page')
);
const CashierShiftPage = lazyWithRetry(
  () => import('../pages/private/shifts/cashier-shift.page')
);
const ConfigurationPage = lazyWithRetry(
  () => import('../pages/private/configuration/configuration.page')
);
const ConfigurationPOSPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/invoice-template/invoice-template.page'
    )
);
const BarcodeLabelPage = lazyWithRetry(
  () =>
    import('../pages/private/configuration/barcode-label/barcode-label.page')
);
const CustomersPage = lazyWithRetry(
  () => import('../pages/private/customers/customers.page')
);
const ProvidersPage = lazyWithRetry(
  () => import('../pages/private/providers/providers.page')
);
const BusinessPage = lazyWithRetry(
  () => import('../pages/private/configuration/bussiness/business.page')
);
const BusinessSettingsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/business-settings/business-settings.page'
    )
);
const SalesVendorsPage = lazyWithRetry(
  () =>
    import('../pages/private/configuration/sales-vendors/sales-vendors.page')
);
const CommissionCampaignsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/commission-campaigns/commission-campaigns.page'
    )
);
const AccountingScopePage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/accounting-scope/accounting-scope.page'
    )
);

// User pages
const ProfilePage = lazyWithRetry(
  () => import('../pages/private/profile/profile.page')
);
const AccountPage = lazyWithRetry(
  () => import('../pages/private/account/account.page')
);
const CustomerBillingPage = lazyWithRetry(
  () => import('../pages/private/account/billing/customer-billing.page')
);
// Report pages
const SalesReportPage = lazyWithRetry(
  () => import('../pages/private/reports/sales-report.page')
);
const InventoryReportPage = lazyWithRetry(
  () => import('../pages/private/reports/inventory-report.page')
);
const UserActivityPage = lazyWithRetry(
  () => import('../pages/private/reports/user-activity.page')
);
const FinancialSummaryPage = lazyWithRetry(
  () => import('../pages/private/reports/financial-summary.page')
);
const FinancialDetailPage = lazyWithRetry(
  () => import('../pages/private/reports/financial-detail.page')
);
const DailySummaryPage = lazyWithRetry(
  () => import('../pages/private/reports/daily-summary.page')
);

const PaymentTypesReportPage = lazyWithRetry(
  () => import('../pages/private/reports/payment-type-report.page')
);
const PaymentTypeDetailPage = lazyWithRetry(
  () => import('../pages/private/reports/payment-type-detail.page')
);
const DgiiReportsPage = lazyWithRetry(
  () => import('../pages/private/reports/dgii-reports.page')
);
const SalesCommissionReportPage = lazyWithRetry(
  () => import('../pages/private/reports/sales-commission.page')
);
const PropinaReportPage = lazyWithRetry(
  () => import('../pages/private/reports/propina-report.page')
);
const FinanzasPage = lazyWithRetry(
  () => import('../pages/private/finanzas/finanzas.page')
);
const ContabilidadPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/contabilidad.page')
);
const ConfigurationContabilidadPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/configuration-contabilidad.page'
    )
);
const AccountingNotificationsPage = lazyWithRetry(
  () =>
    import('../pages/private/configuration/contabilidad/notificaciones.page')
);
const ChartOfAccountsPage = lazyWithRetry(
  () =>
    import('../pages/private/configuration/contabilidad/chart-of-accounts.page')
);
const ExpenseCategoriesPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/expense-categories.page'
    )
);
const AdjustmentReasonsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/adjustment-reasons.page'
    )
);
const CostCentersPage = lazyWithRetry(
  () => import('../pages/private/configuration/contabilidad/cost-centers.page')
);
const AccountingConceptsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/accounting-concepts.page'
    )
);
const PaymentTypesPage = lazyWithRetry(
  () => import('../pages/private/configuration/contabilidad/payment-types.page')
);
const FiscalYearsPage = lazyWithRetry(
  () => import('../pages/private/configuration/contabilidad/fiscal-years.page')
);
const AccountingPeriodsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/accounting-periods.page'
    )
);
const PeriodCloseConfigPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/period-close-config.page'
    )
);
const BankAccountsPage = lazyWithRetry(
  () => import('../pages/private/configuration/contabilidad/bank-accounts.page')
);
const AccountingDefaultsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/accounting-defaults.page'
    )
);
const TaxAccountMapPage = lazyWithRetry(
  () =>
    import('../pages/private/configuration/contabilidad/tax-account-map.page')
);
const PurchaseExpenseAccountsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/purchase-expense-accounts.page'
    )
);
const PostingRulesPage = lazyWithRetry(
  () => import('../pages/private/configuration/contabilidad/posting-rules.page')
);
const AccountingBackfillPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/accounting-backfill.page'
    )
);
const FixedAssetCategoriesPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/contabilidad/fixed-asset-categories.page'
    )
);
const FixedAssetsPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/fixed-assets.page')
);
const BudgetsPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/budgets.page')
);
const BudgetDetailPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/budget-detail.page')
);
const SyncingPage = lazyWithRetry(
  () => import('../pages/private/configuration/syncing/syncing.page')
);
const GastosPage = lazyWithRetry(
  () => import('../pages/private/finanzas/gastos.page')
);
const CuentasPorCobrarPage = lazyWithRetry(
  () => import('../pages/private/finanzas/cuentas-por-cobrar.page')
);
const CuentasPorPagarPage = lazyWithRetry(
  () => import('../pages/private/finanzas/cuentas-por-pagar.page')
);
const CalendarioPage = lazyWithRetry(
  () => import('../pages/private/finanzas/calendario.page')
);
const DiarioGeneralPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/diario-general.page')
);
const AsientosPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/asientos.page')
);
const MayorGeneralPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/mayor-general.page')
);
const DiariosCanceladosPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/diarios-cancelados.page')
);
const ConciliacionBancariaPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/conciliacion-bancaria.page')
);
const ActivosFijosPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/activos-fijos.page')
);
const DepreciacionesPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/depreciaciones.page')
);
const ReportesFinancierosPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/reportes.page')
);
const AuditoriaContablePage = lazyWithRetry(
  () => import('../pages/private/contabilidad/auditoria.page')
);
const DashboardContablePage = lazyWithRetry(
  () => import('../pages/private/contabilidad/dashboard-contable.page')
);
const EstadoSistemaPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/estado-sistema.page')
);
const FinancialHealthPage = lazyWithRetry(
  () => import('../pages/private/contabilidad/financial-health.page')
);
const PaymentsPage = lazyWithRetry(
  () => import('../pages/private/payments/payments.page')
);
const TaxPage = lazyWithRetry(
  () => import('../pages/private/configuration/tax/tax.page')
);
const DeliveryPage = lazyWithRetry(
  () => import('../pages/private/configuration/delivery/delivery.page')
);
const NCFPage = lazyWithRetry(
  () => import('../pages/private/configuration/ncf/ncf.page')
);
const EcfOnboardingPage = lazyWithRetry(
  () =>
    import('../pages/private/configuration/ecf-onboarding/ecf-onboarding.page')
);
const EcfBatchPage = lazyWithRetry(
  () => import('../pages/private/configuration/ecf-batch/ecf-batch.page')
);
const EcfBatchDetailPage = lazyWithRetry(
  () => import('../pages/private/configuration/ecf-batch/ecf-batch-detail.page')
);
const EcfCertificatesPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/ecf-certificates/ecf-certificates.page'
    )
);
const RolePermissionsPage = lazyWithRetry(
  () =>
    import(
      '../pages/private/configuration/role-permissions/role-permissions.page'
    )
);
const ApprovalsPage = lazyWithRetry(
  () => import('../pages/private/configuration/approvals/approvals.page')
);
const PromotionsPage = lazyWithRetry(
  () => import('../pages/private/configuration/promotions/promotions.page')
);
const BranchesPage = lazyWithRetry(
  () => import('../pages/private/configuration/branches/branches.page')
);
const RestaurantFloorPlanPage = lazyWithRetry(
  () => import('../pages/private/restaurant/floor-plan/floor-plan.page')
);
const RestaurantServicePage = lazyWithRetry(
  () => import('../pages/private/restaurant/service/service.page')
);

export const privateRoutes: RouteObject[] = [
  {
    path: '*',
    element: <Navigate to={PATH.NOT_FOUND} />,
  },
  // {
  //   path: '',
  //   element: <SidebarNavigator />,
  // },
  {
    path: PATH.NOT_FOUND,
    element: createSuspendedRoute(<NotFound />),
  },
  {
    path: PRIVATE_PATH.CONTRACT_SIGNING,
    element: createSuspendedRoute(<ContractSigning />),
  },
  {
    path: PRIVATE_PATH.DASHBOARD,
    element: createSuspendedRoute(<Dashboard />),
  },

  {
    path: PRIVATE_PATH.POS,
    element: createSuspendedRoute(
      <PlanGuard
        requiredPlan="FREE"
        sku="module.pos"
        paywallFeatures={[
          'Punto de venta táctil para caja',
          'Apertura/cierre de turnos por cajero',
          'Cobro mixto (efectivo, tarjeta, transferencia)',
          'Impresión directa a impresora térmica',
        ]}
      >
        <POS />
      </PlanGuard>
    ),
  },
  {
    path: PRIVATE_PATH.INVOICES_EDIT(),
    element: createSuspendedRoute(<Invoice />),
  },
  {
    path: PRIVATE_PATH.INVOICES,
    element: createSuspendedRoute(<InvoiceList />),
  },
  {
    path: PRIVATE_PATH.QUOTES,
    element: createSuspendedRoute(<InvoiceList />),
  },
  {
    path: PRIVATE_PATH.CONDUCES,
    element: createSuspendedRoute(<InvoiceList />),
  },
  {
    path: PRIVATE_PATH.SALES_ORDERS,
    element: createSuspendedRoute(<SalesOrdersPage />),
  },
  {
    path: `${PRIVATE_PATH.SALES_ORDERS}/new`,
    element: createSuspendedRoute(<SalesOrdersPage />),
  },
  {
    path: PRIVATE_PATH.SALES_ORDER_DETAIL(),
    element: createSuspendedRoute(<SalesOrdersPage />),
  },
  {
    path: PRIVATE_PATH.RENTAL_PEDIDOS,
    element: createSuspendedRoute(
      <RentalsAddonGate>
        <RentalListPage />
      </RentalsAddonGate>
    ),
  },
  {
    path: `${PRIVATE_PATH.RENTAL_PEDIDOS}/new`,
    element: createSuspendedRoute(
      <RentalsAddonGate>
        <RentalDetailPage />
      </RentalsAddonGate>
    ),
  },
  {
    path: PRIVATE_PATH.RENTAL_PEDIDO_DETAIL(),
    element: createSuspendedRoute(
      <RentalsAddonGate>
        <RentalDetailPage />
      </RentalsAddonGate>
    ),
  },
  {
    path: PRIVATE_PATH.RENTAL_CALENDAR,
    element: createSuspendedRoute(
      <RentalsAddonGate>
        <RentalsCalendarPage />
      </RentalsAddonGate>
    ),
  },
  {
    path: PRIVATE_PATH.RENTAL_RECOGIDO,
    element: createSuspendedRoute(
      <RentalsAddonGate>
        <RentalsRecogidoPage />
      </RentalsAddonGate>
    ),
  },
  {
    path: PRIVATE_PATH.PAYMENTS,
    element: createSuspendedRoute(<PaymentsPage />),
  },
  {
    path: PRIVATE_PATH.APPROVALS,
    element: createSuspendedRoute(<ApprovalsPage />),
  },
  {
    // Deep-link from notification actions. Reuses ApprovalsPage —
    // page reads the :id param and auto-opens the detail dialog.
    path: PRIVATE_PATH.APPROVAL_DETAIL(),
    element: createSuspendedRoute(<ApprovalsPage />),
  },
  {
    path: '/restaurant',
    children: [
      {
        path: 'floor-plan',
        element: createSuspendedRoute(<RestaurantFloorPlanPage />),
      },
      {
        path: 'service',
        element: createSuspendedRoute(<RestaurantServicePage />),
      },
    ],
  },
  {
    path: PRIVATE_PATH.INVENTORY_PRODUCTS,
    element: createSuspendedRoute(<Products />),
  },
  {
    path: PRIVATE_PATH.INVENTORY_PRODUCT(),
    element: createSuspendedRoute(<EditProduct />),
  },
  {
    path: PRIVATE_PATH.INVENTORY_PRODUCT_INGREDIENTS(),
    element: createSuspendedRoute(<ProductIngredients />),
  },
  {
    path: PRIVATE_PATH.INVENTORY_CATEGORIES,
    element: createSuspendedRoute(<Categories />),
  },
  {
    path: PRIVATE_PATH.INVENTORY_WAREHOUSE,
    element: createSuspendedRoute(<Warehouse />),
  },
  {
    path: PRIVATE_PATH.INVENTORY_KDS,
    element: createSuspendedRoute(
      <PlanGuard
        requiredPlan="STARTUP"
        sku="addon.kds"
        paywallFeatures={[
          'Tablero KDS por estación (cocina, bar, etc.)',
          'Workflows jerárquicos con auto-avance',
          'Métricas por nodo + lifecycle por factura',
          'Asignación de accesos por usuario',
        ]}
      >
        <KdsPage />
      </PlanGuard>
    ),
  },
  {
    path: PRIVATE_PATH.KDS_REPORTS,
    element: createSuspendedRoute(<KdsReportsPage />),
  },
  {
    // Legacy `/inventory/kds` path — redirect to the new top-level
    // route so old bookmarks / sidebar caches keep working.
    path: PRIVATE_PATH.INVENTORY_KDS_LEGACY,
    element: <Navigate to={PRIVATE_PATH.KDS} replace />,
  },
  {
    path: PRIVATE_PATH.INVENTORY_STOCK_MOVEMENT,
    element: createSuspendedRoute(<StockMovementPage />),
  },
  {
    path: PRIVATE_PATH.FOLLOW_UP_REMINDERS,
    element: createSuspendedRoute(<FollowUpRemindersPage />),
  },

  {
    path: PRIVATE_PATH.SUPPLIER_INVOICES,
    element: createSuspendedRoute(<PurchaseOrdersPage />),
  },
  {
    path: PRIVATE_PATH.SUPPLIER_INVOICE(),
    element: createSuspendedRoute(<EditPurchaseOrdersRoute />),
  },
  // Phase 1 back-compat — legacy /inventory/purchase-orders bookmarks
  // 301-style redirect to the renamed surface. Kept until every
  // consumer (docs, deep links, exports) is migrated; Phase 7 removes.
  {
    path: '/inventory/purchase-orders',
    element: <Navigate to={PRIVATE_PATH.SUPPLIER_INVOICES} replace />,
  },
  {
    path: '/inventory/purchase-orders/:id',
    element: createSuspendedRoute(<EditPurchaseOrdersRoute />),
  },
  {
    path: PRIVATE_PATH.PROCUREMENT_PURCHASE_ORDERS,
    element: createSuspendedRoute(<ProcurementPurchaseOrdersPage />),
  },
  {
    // Same param route matches both `/purchase-orders/new` and
    // `/purchase-orders/:id`. The page reads `id === 'new'` to switch
    // between create and edit modes.
    path: PRIVATE_PATH.PROCUREMENT_PURCHASE_ORDER(),
    element: createSuspendedRoute(<EditProcurementPurchaseOrderPage />),
  },
  {
    path: PRIVATE_PATH.SUPPLIER_TAX_DOCUMENT(),
    element: createSuspendedRoute(<SupplierTaxDocumentDetailPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION,
    element: createSuspendedRoute(<ConfigurationPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_BUSINESS,
    element: createSuspendedRoute(<BusinessPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_BUSINESS_SETTINGS,
    element: createSuspendedRoute(<BusinessSettingsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_SALES_VENDORS,
    element: createSuspendedRoute(<SalesVendorsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_COMMISSION_CAMPAIGNS,
    element: createSuspendedRoute(<CommissionCampaignsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_SCOPE,
    element: createSuspendedRoute(<AccountingScopePage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_TAX,
    element: createSuspendedRoute(<TaxPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_DELIVERY,
    element: createSuspendedRoute(<DeliveryPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_PROMOTIONS,
    element: createSuspendedRoute(<PromotionsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_POS,
    element: createSuspendedRoute(<ConfigurationPOSPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_BARCODE_LABEL,
    element: createSuspendedRoute(<BarcodeLabelPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_NCF,
    element: createSuspendedRoute(<NCFPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ECF_ONBOARDING,
    element: createSuspendedRoute(<EcfOnboardingPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ECF_BATCH,
    element: createSuspendedRoute(<EcfBatchPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ECF_BATCH_DETAIL(),
    element: createSuspendedRoute(<EcfBatchDetailPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ECF_CERTIFICATES,
    element: createSuspendedRoute(<EcfCertificatesPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ROLE_PERMISSIONS,
    element: createSuspendedRoute(<RolePermissionsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_USERS,
    element: createSuspendedRoute(<UsersPage />),
  },
  {
    path: PRIVATE_PATH.OPEN_CLOSE_SHIFT,
    element: createSuspendedRoute(<CashierShiftPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_SHIFTS,
    element: createSuspendedRoute(<ShiftsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_BRANCHES,
    element: createSuspendedRoute(<BranchesPage />),
  },
  {
    path: PRIVATE_PATH.CUSTOMERS,
    element: createSuspendedRoute(<CustomersPage />),
  },
  {
    path: PRIVATE_PATH.PROVIDERS,
    element: createSuspendedRoute(<ProvidersPage />),
  },
  // User
  {
    path: PRIVATE_PATH.PROFILE,
    element: createSuspendedRoute(<ProfilePage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNT,
    element: createSuspendedRoute(<AccountPage />),
  },
  // /account/billing — PR-19 billing_invoice list + drill-down.
  // The legacy redirect lived here when the original billing flow
  // was inlined into /account; PR-20 brings it back as a dedicated
  // customer-facing surface backed by the new billing_invoice table.
  {
    path: '/account/billing',
    element: createSuspendedRoute(<CustomerBillingPage />),
  },
  // Reports
  {
    path: PRIVATE_PATH.REPORTS_SALES,
    element: createSuspendedRoute(<SalesReportPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_INVENTORY,
    element: createSuspendedRoute(<InventoryReportPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_USER_ACTIVITY,
    element: createSuspendedRoute(<UserActivityPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_FINANCIAL,
    element: createSuspendedRoute(<FinancialSummaryPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_FINANCIAL_DETAIL(),
    element: createSuspendedRoute(<FinancialDetailPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_DAILY,
    element: createSuspendedRoute(<DailySummaryPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_PAYMENT_TYPES,
    element: createSuspendedRoute(<PaymentTypesReportPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_PAYMENT_TYPE_DETAIL(),
    element: createSuspendedRoute(<PaymentTypeDetailPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_DGII,
    element: createSuspendedRoute(<DgiiReportsPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_SALES_COMMISSION,
    element: createSuspendedRoute(<SalesCommissionReportPage />),
  },
  {
    path: PRIVATE_PATH.REPORTS_PROPINA,
    element: createSuspendedRoute(<PropinaReportPage />),
  },
  // Finanzas (PR-1 shell — children land in PR-3)
  {
    path: PRIVATE_PATH.FINANCE,
    element: createSuspendedRoute(<FinanzasPage />),
  },
  // Contabilidad (PR-1 shell — children land in PR-5 onward)
  {
    path: PRIVATE_PATH.ACCOUNTING,
    element: createSuspendedRoute(<ContabilidadPage />),
  },
  // Configuración → Contabilidad (PR-1 shell — sub-pages land in PR-4 onward)
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING,
    element: createSuspendedRoute(<ConfigurationContabilidadPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_NOTIFICATIONS,
    element: createSuspendedRoute(<AccountingNotificationsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_COA,
    element: createSuspendedRoute(<ChartOfAccountsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_EXPENSE_CATEGORIES,
    element: createSuspendedRoute(<ExpenseCategoriesPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_ADJUSTMENT_REASONS,
    element: createSuspendedRoute(<AdjustmentReasonsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_COST_CENTERS,
    element: createSuspendedRoute(<CostCentersPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_CONCEPTS,
    element: createSuspendedRoute(<AccountingConceptsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_PAYMENT_TYPES,
    element: createSuspendedRoute(<PaymentTypesPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_FISCAL_YEARS,
    element: createSuspendedRoute(<FiscalYearsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_PERIODS,
    element: createSuspendedRoute(<AccountingPeriodsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_PERIOD_CLOSE,
    element: createSuspendedRoute(<PeriodCloseConfigPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_BANK_ACCOUNTS,
    element: createSuspendedRoute(<BankAccountsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_DEFAULTS,
    element: createSuspendedRoute(<AccountingDefaultsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_TAX_MAP,
    element: createSuspendedRoute(<TaxAccountMapPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_PURCHASE_EXPENSE,
    element: createSuspendedRoute(<PurchaseExpenseAccountsPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_POSTING_RULES,
    element: createSuspendedRoute(<PostingRulesPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_BACKFILL,
    element: createSuspendedRoute(<AccountingBackfillPage />),
  },
  {
    path: PRIVATE_PATH.CONFIGURATION_ACCOUNTING_FA_CATEGORIES,
    element: createSuspendedRoute(<FixedAssetCategoriesPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_FIXED_ASSETS,
    element: createSuspendedRoute(<FixedAssetsPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_BUDGETS,
    element: createSuspendedRoute(<BudgetsPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_BUDGET_DETAIL,
    element: createSuspendedRoute(<BudgetDetailPage />),
  },
  // Configuración → Conciliación de Sincronización
  {
    path: PRIVATE_PATH.CONFIGURATION_SYNCING,
    element: createSuspendedRoute(<SyncingPage />),
  },
  // Finanzas children (PR-1 stubs — logic lands in PR-3)
  {
    // Gastos promoted to a top-level entry — PRIVATE_PATH.FINANCE_EXPENSES
    // now points at /expenses. Keep the legacy /finance/expenses path
    // routed to the same page so existing deep links + bookmarks survive.
    path: PRIVATE_PATH.FINANCE_EXPENSES,
    element: createSuspendedRoute(<GastosPage />),
  },
  {
    path: '/finance/expenses',
    element: createSuspendedRoute(<GastosPage />),
  },
  {
    path: PRIVATE_PATH.FINANCE_AR,
    element: createSuspendedRoute(<CuentasPorCobrarPage />),
  },
  {
    path: PRIVATE_PATH.FINANCE_AP,
    element: createSuspendedRoute(<CuentasPorPagarPage />),
  },
  {
    path: PRIVATE_PATH.FINANCE_CALENDAR,
    element: createSuspendedRoute(<CalendarioPage />),
  },
  // Contabilidad children (PR-1 stubs — logic lands in PR-5+)
  {
    path: PRIVATE_PATH.ACCOUNTING_DAILY_JOURNAL,
    element: createSuspendedRoute(<DiarioGeneralPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_JOURNAL,
    element: createSuspendedRoute(<AsientosPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_GENERAL_LEDGER,
    element: createSuspendedRoute(<MayorGeneralPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_CANCELLED_JOURNAL,
    element: createSuspendedRoute(<DiariosCanceladosPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_BANK_RECONCILIATION,
    element: createSuspendedRoute(<ConciliacionBancariaPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_FIXED_ASSETS,
    element: createSuspendedRoute(<ActivosFijosPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_DEPRECIATIONS,
    element: createSuspendedRoute(<DepreciacionesPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_REPORTS,
    element: createSuspendedRoute(<ReportesFinancierosPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_AUDIT,
    element: createSuspendedRoute(<AuditoriaContablePage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_DASHBOARD,
    element: createSuspendedRoute(<DashboardContablePage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_SYSTEM_STATUS,
    element: createSuspendedRoute(<EstadoSistemaPage />),
  },
  {
    path: PRIVATE_PATH.ACCOUNTING_FINANCIAL_HEALTH,
    element: createSuspendedRoute(<FinancialHealthPage />),
  },
];
